import React, { useRef, useState } from "react";
import type { Step, HubSpotProduct, ItemImage } from "../../types/configurator";
import { formatPrice } from "../../utils";

interface Props {
  step: Step;
  isDragOver: boolean;
  productMap: Record<string, HubSpotProduct>;
  itemImageMap: Record<string, ItemImage[]>;
  onRemoveProduct: (stepId: string, itemUid: string) => void;
  onItemImageUpload: (itemKey: string, file: File) => Promise<void>;
  onItemImageDelete: (imageId: string, itemKey: string) => Promise<void>;
  onDrop: (e: React.DragEvent, stepId: string) => void;
  onDragOver: (e: React.DragEvent, stepId: string) => void;
  onDragLeave: () => void;
}

function ItemImageGallery({
  itemKey,
  images,
  onUpload,
  onDelete,
}: {
  itemKey: string;
  images: ItemImage[];
  onUpload: (itemKey: string, file: File) => Promise<void>;
  onDelete: (imageId: string, itemKey: string) => Promise<void>;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(itemKey, file);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="step-image-gallery" style={{ marginTop: 4 }}>
      <div className="step-image-gallery-grid">
        {images.map((img) => (
          <div key={img.id} className="step-image-thumb">
            <img src={img.url} alt="" />
            <button
              className="step-image-delete"
              title="Delete image"
              onClick={() => onDelete(img.id, itemKey)}
            >
              ✕
            </button>
          </div>
        ))}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <button
          className="step-image-upload-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? (
            <span style={{ animation: 'spin 0.8s linear infinite', display: 'inline-block' }}>⟳</span>
          ) : (
            <>+ Image</>
          )}
        </button>
      </div>
    </div>
  );
}

function FieldValueItem({
  item,
  stepId,
  itemImages,
  onRemoveProduct,
  onItemImageUpload,
  onItemImageDelete,
}: {
  item: Extract<Step['items'][number], { type: 'field_value' }>;
  stepId: string;
  itemImages: ItemImage[];
  onRemoveProduct: (stepId: string, uid: string) => void;
  onItemImageUpload: (itemKey: string, file: File) => Promise<void>;
  onItemImageDelete: (imageId: string, itemKey: string) => Promise<void>;
}) {
  const itemKey = `field_value:${item.field}:${item.value}`;
  const firstImage = itemImages[0];

  return (
    <div className="step-product" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {firstImage ? (
          <img
            src={firstImage.url}
            alt={item.label}
            style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
          />
        ) : (
          <span style={{ width: 32, height: 32, flexShrink: 0 }} />
        )}
        <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
        <span className="prod-price field-tag">{item.field}</span>
        <button
          className="remove-product"
          onClick={() => onRemoveProduct(stepId, item.uid)}
        >
          ×
        </button>
      </div>
      <ItemImageGallery
        itemKey={itemKey}
        images={itemImages}
        onUpload={onItemImageUpload}
        onDelete={onItemImageDelete}
      />
    </div>
  );
}

function ProductItem({
  item,
  product,
  stepId,
  itemImages,
  onRemoveProduct,
  onItemImageUpload,
  onItemImageDelete,
}: {
  item: Extract<Step['items'][number], { type: 'product' }>;
  product: HubSpotProduct;
  stepId: string;
  itemImages: ItemImage[];
  onRemoveProduct: (stepId: string, uid: string) => void;
  onItemImageUpload: (itemKey: string, file: File) => Promise<void>;
  onItemImageDelete: (imageId: string, itemKey: string) => Promise<void>;
}) {
  const itemKey = `product:${item.productId}`;
  const displayImage = product.imageUrl || itemImages[0]?.url;

  return (
    <div className="step-product" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {displayImage && (
          <img
            src={displayImage}
            alt={product.name}
            style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
          />
        )}
        <span style={{ flex: 1, minWidth: 0 }}>{product.name}</span>
        <span className="prod-price">{formatPrice(product.price)}</span>
        <button
          className="remove-product"
          onClick={() => onRemoveProduct(stepId, item.uid)}
        >
          ×
        </button>
      </div>
      <ItemImageGallery
        itemKey={itemKey}
        images={itemImages}
        onUpload={onItemImageUpload}
        onDelete={onItemImageDelete}
      />
    </div>
  );
}

export const ProductDropZone: React.FC<Props> = ({
  step,
  isDragOver,
  productMap,
  itemImageMap,
  onRemoveProduct,
  onItemImageUpload,
  onItemImageDelete,
  onDrop,
  onDragOver,
  onDragLeave,
}) => {
  return (
    <div
      className={`step-items ${isDragOver ? "drag-over" : ""}`}
      onDrop={(e) => onDrop(e, step.id)}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver(e, step.id);
      }}
      onDragLeave={onDragLeave}
    >
      {step.items.length === 0 && (
        <div className="step-items-empty">Drag products or field values here from the catalog</div>
      )}
      {step.items.map((item) => {
        if (item.type === 'product') {
          const p = productMap[item.productId];
          if (!p) return null;
          return (
            <ProductItem
              key={item.uid}
              item={item}
              product={p}
              stepId={step.id}
              itemImages={itemImageMap[`product:${item.productId}`] || []}
              onRemoveProduct={onRemoveProduct}
              onItemImageUpload={onItemImageUpload}
              onItemImageDelete={onItemImageDelete}
            />
          );
        } else {
          return (
            <FieldValueItem
              key={item.uid}
              item={item}
              stepId={step.id}
              itemImages={itemImageMap[`field_value:${item.field}:${item.value}`] || []}
              onRemoveProduct={onRemoveProduct}
              onItemImageUpload={onItemImageUpload}
              onItemImageDelete={onItemImageDelete}
            />
          );
        }
      })}
    </div>
  );
};
